import { useObserver } from "mobx-react";
import React from "react";
import {
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { Date, Select, Switch, Text } from "@components/formx";
import { Option } from "@components/Select";
import { useConsultantEditorContext } from "../ConsultantEditorContext";
import { ConsultantEditorStore } from "../ConsultantEditorStore";
import { ContractType, LegalPersonType } from "@graphql";
import { moment } from "@bonliva-crm/common";
import { Checkbox } from "@common/formx";

export function ContactTypeInfo() {
  const editorStore: ConsultantEditorStore = useConsultantEditorContext();

  const isSubcontractor = editorStore.legalPersonType === LegalPersonType.Subcontractor;
  const isEmployee = editorStore.legalPersonType === LegalPersonType.Employee;

  const countriesOptions: Option[] = editorStore.allCountriesWorldwide
    .filter((c) => c.onTopInSearch)
    .sortBy((c) => c.sort)
    .toLabelValue(
      (c) => locale.getCountryName(c.name),
      (c) => c.name
    );

  countriesOptions.push({
    label: <Divider />,
    value: "div1",
    disabled: true
  });

  countriesOptions.push(
    ...editorStore.allCountriesWorldwide
      .filter((c) => !c.onTopInSearch)
      .sort((a, b) => (a.name < b.name ? -1 : 1))
      .map((c) => ({
        label: locale.getCountryName(c.name),
        value: c.name
      }))
  );

  const radioOptions = [
    // {
    //   label: "Ingen vald",
    //   value: null
    // },
    {
      label: locale.legalPersonType[LegalPersonType.Employee],
      value: LegalPersonType.Employee
    },
    {
      label: locale.legalPersonType[LegalPersonType.Subcontractor],
      value: LegalPersonType.Subcontractor
    }
  ];

  return useObserver(() => {
    const { contactType } = editorStore.form.values;

    const showExternalTaxNumber =
      contactType.businessAddress?.country &&
      !contactType.businessAddress?.country.includes("Sweden");

    return (
      <div>
        <div>
          <FormControl>
            <RadioGroup
              name="contactType.type"
              row
              value={editorStore.legalPersonType}
              onChange={(event: React.ChangeEvent<{ value: string }>) =>
                editorStore.updateLegalPersonType(event.target.value as LegalPersonType)
              }
            >
              {radioOptions.map((x) => (
                <FormControlLabel
                  key={x.value}
                  control={<Radio />}
                  label={x.label}
                  value={x.value}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </div>

        <Collapse in={isEmployee}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Select
                name="contactType.employeeContractType"
                label="Val 1/2/3"
                options={Object.keys(ContractType)
                  .filter((x) => x !== ContractType.NationalProcurement)
                  .toLabelValue((x) => locale.contractType[x])}
              ></Select>
            </Grid>

            {editorStore.canViewAccountNumber && (
              <Grid item xs={12}>
                <Text
                  label={locale.ClearingNumber}
                  name="contactType.clearingNumber"
                  disabled={!editorStore.canEditAccountNumber}
                />
              </Grid>
            )}

            {editorStore.canViewAccountNumber && (
              <Grid item xs={12}>
                <Text
                  label="Kontonummer"
                  name="contactType.accountNumber"
                  disabled={!editorStore.canEditAccountNumber}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Text
                label={"Löneanspråk, grundersättning per timme"}
                name="contactType.employeeSalaryClaim"
                TextFieldProps={{
                  type: "number"
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Text
                label={locale.Rate}
                name="contactType.rate"
                TextFieldProps={{
                  inputProps: { min: 0, max: 100 },
                  type: "number",
                  InputProps: {
                    startAdornment: <InputAdornment position="start">%</InputAdornment>
                  }
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Text
                label={"Förhöjd skattesats"}
                name="contactType.increasedRate"
                TextFieldProps={{
                  inputProps: { min: 0, max: 100 },
                  type: "number",
                  InputProps: {
                    startAdornment: <InputAdornment position="start">%</InputAdornment>
                  }
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Date
                utc
                key={"StartDate"}
                DatePickerProps={{
                  startDate: moment().toDate(),
                  shouldCloseOnSelect: true
                }}
                name="contactType.startDate"
                label={locale.StartDate}
              />
            </Grid>
            <Grid item xs={4}>
              <Date
                utc
                key={"EndDate"}
                DatePickerProps={{
                  minDate: moment(
                    editorStore.form.getValue("contactType.startDate")
                  ).toDate(),
                  startDate: moment().toDate(),
                  shouldCloseOnSelect: true
                }}
                name="contactType.endDate"
                label={locale.EndDate}
              />
            </Grid>
          </Grid>
        </Collapse>

        <Collapse in={isSubcontractor}>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Text label={locale["Organisation number"]} name="contactType.ssn" />
            </Grid>
            <Grid item xs={4}>
              <Checkbox
                CheckboxProps={{ disabled: !editorStore.canEditAccountNumber }}
                name={"contactType.FTaxChecked"}
                label={locale.FTaxChecked}
              />
            </Grid>
            <Grid item xs={4}>
              <Checkbox
                CheckboxProps={{ disabled: !editorStore.canEditAccountNumber }}
                name={"contactType.VATChecked"}
                label={locale.VATChecked}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Select
                label={locale.Country}
                name="contactType.businessAddress.country"
                options={countriesOptions}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text
                name="contactType.businessAddress.zipCode"
                label={locale["ZIP code"]}
                TextFieldProps={{
                  fullWidth: true
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text
                name="contactType.businessAddress.city"
                label={locale.City}
                TextFieldProps={{
                  fullWidth: true
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text
                name="contactType.businessAddress.streetName"
                label={locale["Street address"]}
                TextFieldProps={{
                  fullWidth: true
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Text label={locale.CompanyName} name="contactType.companyName" />
            </Grid>

            <Grid item xs={6} sm={6}>
              <Text
                label={locale.emailForSelfInvoicing}
                name="contactType.emailForSelfInvoicing"
                TextFieldProps={{
                  type: "email"
                }}
              />
            </Grid>
          </Grid>
          {showExternalTaxNumber && (
            <Grid item xs={12} sm={12}>
              <Text
                label={locale.ExternalTaxNumber}
                name="contactType.externalTaxNumber"
                TextFieldProps={{
                  fullWidth: true
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Text
              label={"Ersättningsanspråk, grundersättning per timme"}
              name="contactType.subcontractorSalaryClaim"
              TextFieldProps={{
                type: "number"
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Switch
              name="contactType.receiveWorkBreakdownByEmail"
              label={locale.receiveWorkBreakdownByEmail}
            />
          </Grid>
        </Collapse>
      </div>
    );
  });
}
